import format from 'date-fns/format'
import { parseISO } from 'date-fns'

import {
  IPowerPlantSystemSelectAllOption,
  IPowerPlantSystemGroupedOption,
  IFormattedSystemsForPostRequest,
  IPowerPlantSystemGroupedByTypeOfEquipment,
  DownloadsData,
} from '../../../domain/downloads.types'
import { EQUIPMENT_TYPE_LABEL, SYSTEM_LABEL_INFO } from '../../../domain/enums/downloads.enums'
import { DATE_FORMAT } from '../../../infrastructure/enums/dateFormat.enums'
import { t, UPPERCASE } from '../../../infrastructure/translation/customTranslation'
import { SELECT_ALL_OPTION_VALUE } from '../../../constants/downloads.constants'

/**
 * formats a date to a string in the format 'yyyy-MM-dd'.
 * @param date
 * @returns date in string format
 */
export const formatDownloadDate = (date: Date): string => {
  const outputFormat = DATE_FORMAT.DATE_EN
  const outputDate = format(date, outputFormat)
  return outputDate
}

const setSystemLabelValue = (system: DownloadsData): string => {
  let systemLabel = ''
  const equipmentLabel = system.Equipment.equipment_type.label
  const EquipmentReplacement = system.removal_date
    ? ` (${t('replaced_the')} ${format(
        parseISO(system.removal_date),
        DATE_FORMAT.DATE_FR_NO_TIME_SEPARATE_BY_SLASH
      )})`
    : ''
  switch (equipmentLabel) {
    case EQUIPMENT_TYPE_LABEL.METER:
      systemLabel = SYSTEM_LABEL_INFO.PRISME
      break
    case EQUIPMENT_TYPE_LABEL.METEO:
      systemLabel = SYSTEM_LABEL_INFO.EXTERNAL_METEO
      break
    case EQUIPMENT_TYPE_LABEL.INVERTER:
      systemLabel = ` (${system.Equipment?.equipment_model?.equipment_brand?.label} ${system.Equipment?.equipment_model?.label})`
      break
    case EQUIPMENT_TYPE_LABEL.CAPTEUR:
    default:
      systemLabel = ''
  }
  const systemTranslate: {
    [key in EQUIPMENT_TYPE_LABEL]: string
  } = {
    [EQUIPMENT_TYPE_LABEL.ONDULEUR]: 'Onduleur',
    [EQUIPMENT_TYPE_LABEL.CAPTEUR]: 'Capteur',
    [EQUIPMENT_TYPE_LABEL.COMPTEUR]: 'Compteur',
    [EQUIPMENT_TYPE_LABEL.METEO]: 'Météo',
    [EQUIPMENT_TYPE_LABEL.HYGROMETER]: t('hygrometer'),
    [EQUIPMENT_TYPE_LABEL.SISENSOR]: t('sisensor'),
    [EQUIPMENT_TYPE_LABEL.TEMPSENSOR]: t('tempsensor'),
    [EQUIPMENT_TYPE_LABEL.PYRANOMETER]: t('pyranometer'),
    [EQUIPMENT_TYPE_LABEL.METER]: t('meter'),
    [EQUIPMENT_TYPE_LABEL.INVERTER]: t('inverter'),
  }
  return `${systemTranslate[system.Equipment.equipment_type.label as EQUIPMENT_TYPE_LABEL]}
  (${system.position_number ? `${system.position_number} - ` : ``}${
    system.Equipment.serial
  })${systemLabel}${EquipmentReplacement}`
}

/**
 * return system sort by category
 * includes only active systems (removalDate === null)
 * @param sortFormattedPowerPlantSystems
 * @returns
 */
const sortFormattedSystemsByLabel = (
  sortFormattedPowerPlantSystems: IPowerPlantSystemSelectAllOption[]
): IPowerPlantSystemGroupedByTypeOfEquipment =>
  sortFormattedPowerPlantSystems.reduce(
    (acc: IPowerPlantSystemGroupedByTypeOfEquipment, system: IPowerPlantSystemSelectAllOption) => {
      const { category } = system
      acc[category] = [...(acc[category] || []), system]
      return acc
    },
    {}
  )

/**
 * format power plant systems for select input label
 * return an object with key = systems category and value = array of objects
 * with value = serial and label = system label
 * @param powerPlantSystems
 * @returns
 */
export const formattedPowerPlantSystemsForSelectInput = (
  powerPlantSystems: DownloadsData[]
): IPowerPlantSystemGroupedByTypeOfEquipment => {
  const formattedSystems = powerPlantSystems.map(
    (system: DownloadsData): IPowerPlantSystemSelectAllOption => {
      return {
        value: system.Equipment.serial,
        label: setSystemLabelValue(system),
        category: system.Equipment.equipment_type.label,
        positionNumber: system.position_number,
      }
    }
  )
  return sortFormattedSystemsByLabel(formattedSystems as IPowerPlantSystemSelectAllOption[])
}

const isEquipmentTypeInGroupedOptions = (
  equipmentTypeLabel: string,
  groupedOptions: IPowerPlantSystemGroupedOption[]
): boolean => {
  const isEquipmentTypeFound = groupedOptions.some(
    (groupedOption) => groupedOption.label === equipmentTypeLabel
  )
  return isEquipmentTypeFound
}

const groupedOptionsSorted = (
  groupedOptions: IPowerPlantSystemGroupedOption[]
): IPowerPlantSystemGroupedOption[] => {
  const equipmentTypeOrder = [
    EQUIPMENT_TYPE_LABEL.COMPTEUR,
    EQUIPMENT_TYPE_LABEL.METEO,
    EQUIPMENT_TYPE_LABEL.INVERTER,
    EQUIPMENT_TYPE_LABEL.CAPTEUR,
    EQUIPMENT_TYPE_LABEL.HYGROMETER,
    EQUIPMENT_TYPE_LABEL.SISENSOR,
    EQUIPMENT_TYPE_LABEL.TEMPSENSOR,
    EQUIPMENT_TYPE_LABEL.PYRANOMETER,
    EQUIPMENT_TYPE_LABEL.METER,
  ]

  const sortedGroupedOptions = equipmentTypeOrder
    .map((equipmentType) => {
      if (isEquipmentTypeInGroupedOptions(equipmentType, groupedOptions)) {
        const groupedOptionObjIndex = groupedOptions.findIndex(
          (groupedOption) => groupedOption.label === equipmentType
        )
        return {
          label: equipmentType,
          options: groupedOptions[groupedOptionObjIndex].options,
        }
      }
      return null
    })
    .filter(Boolean) as IPowerPlantSystemGroupedOption[]

  return sortedGroupedOptions
}

/**
 * group power plant systems by category of systems (inverters, meters, etc.)
 * so that they can be displayed in a select input by category
 * @param powerPlantSystems
 * @returns
 */
export const powerPlantSystemsGroupedOption = (
  powerPlantSystems: IPowerPlantSystemGroupedByTypeOfEquipment
): IPowerPlantSystemGroupedOption[] => {
  const groupedOptions: IPowerPlantSystemGroupedOption[] = []
  const keys = Object.keys(powerPlantSystems)
  keys.forEach((key) => {
    groupedOptions.push({
      label: key,
      options: powerPlantSystems[key],
    })
  })
  return groupedOptionsSorted(groupedOptions)
}

/**
 *
 * @param powerPlantSystems
 * @returns return an array of objects with key = systems category and value = array of serials
 */
export const formattedSystemsForPostRequest = (
  powerPlantSystems: IPowerPlantSystemSelectAllOption[] | null
): IFormattedSystemsForPostRequest => {
  if (!powerPlantSystems) return {}
  return powerPlantSystems.reduce((acc: IFormattedSystemsForPostRequest, system) => {
    const { category, value, positionNumber } = system

    if (!acc[category]) acc[category] = [] as { value: string; positionNumber: number }[]
    if (value) acc[category].push({ value, positionNumber })
    return acc
  }, {})
}

const systemsOptionsRemoveSelectAllOption = (
  powerPlantSelectedOptions: IPowerPlantSystemSelectAllOption[]
): IPowerPlantSystemSelectAllOption[] => {
  return powerPlantSelectedOptions.filter(
    (option: IPowerPlantSystemSelectAllOption) => option.value !== SELECT_ALL_OPTION_VALUE
  )
}

export const flattenSystemsOptions = (
  groups: IPowerPlantSystemGroupedOption[]
): IPowerPlantSystemSelectAllOption[] => {
  const powerPlantSelectedOptions = groups.reduce(
    (acc: IPowerPlantSystemSelectAllOption[], group: IPowerPlantSystemGroupedOption) => {
      return acc.concat(group.options)
    },
    []
  )
  return systemsOptionsRemoveSelectAllOption(powerPlantSelectedOptions)
}

/**
 * Add a select all option to the power plant systems options already grouped and formatted
 * @param groupedPowerPlantSystems
 * @returns groupedPowerPlantSystemsWithSelectAllOption
 */
export const addSelectAllOptionToPowerPlantSystemsOptions = (
  groupedPowerPlantSystems: IPowerPlantSystemGroupedOption[]
): IPowerPlantSystemGroupedOption[] => {
  const selectAllOption: IPowerPlantSystemGroupedOption[] = [
    {
      label: '',
      options: [
        {
          value: SELECT_ALL_OPTION_VALUE,
          label: t('select_all', UPPERCASE),
          category: '',
          positionNumber: 0,
        },
      ],
    },
  ]
  return selectAllOption.concat(groupedPowerPlantSystems)
}

/**
 * sort each power plant systems by position number
 * @param powerPlantSystemsFormatted
 * @returns the received object sorted with each group sorted by position number
 */
export const sortPowerPlantInverterSystemsByPositionNumber = (
  powerPlantSystemsFormatted: IPowerPlantSystemGroupedOption[]
): IPowerPlantSystemGroupedOption[] => {
  powerPlantSystemsFormatted.forEach((system) => {
    system.options.sort((a, b) => {
      return a.positionNumber - b.positionNumber
    })
  })
  return powerPlantSystemsFormatted
}
